import type React from 'react';
import type { JSX } from 'react';
import { withTruncation } from '@/components/shared/hocs/withTruncation';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface TruncatedTextInnerProps {
  text: string;
  className: string;
  isTruncated: boolean;
  truncationRef: React.RefCallback<HTMLElement>;
  checkTruncation: () => void;
}

/**
 * Single-line text that opens a Radix tooltip when truncated. Shared by the
 * primary name and the handshake / URL subtitles so all three get identical
 * tooltip behaviour (KIR-2383, KIR-2985).
 */
const TruncatedTextInner = withTruncation<TruncatedTextInnerProps>(
  ({ text, className, truncationRef, isTruncated, checkTruncation }) => {
    const content = (
      <span
        ref={truncationRef}
        className={`${className} truncate block max-w-full`}
        onMouseEnter={checkTruncation}
        onFocus={checkTruncation}
      >
        {text}
      </span>
    );

    if (!isTruncated) {
      return content;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm break-all">
          {text}
        </TooltipContent>
      </Tooltip>
    );
  }
);

interface MCPNameCellProps {
  name: string;
  /** Handshake server name; rendered as a subtitle when set and ≠ `name` (KIR-2383). */
  serverName?: string | null;
  /** Endpoint URL for HTTP MCPs; rendered as a muted line under the name (KIR-2985). */
  url?: string | null;
}

export function MCPNameCell({
  name,
  serverName,
  url,
}: MCPNameCellProps): JSX.Element {
  // Case-insensitive compare suppresses "Time" vs "time" noise; subtitle
  // renders in original case when divergence is real.
  const subtitle =
    serverName && serverName.toLowerCase() !== name.toLowerCase()
      ? serverName
      : null;

  if (!subtitle && !url) {
    return <TruncatedTextInner text={name} className="font-medium" />;
  }

  return (
    <div className="flex flex-col min-w-0">
      <TruncatedTextInner text={name} className="font-medium" />
      {subtitle && (
        <TruncatedTextInner
          text={subtitle}
          className="text-xs text-muted-foreground"
        />
      )}
      {url && (
        <TruncatedTextInner
          text={url}
          className="text-xs text-muted-foreground font-mono"
        />
      )}
    </div>
  );
}
